import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import { UPLOAD_URL } from '../../config';
import './Enchapes.css';

const formatMoney = (v) => (v || v === 0)
  ? new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v)
  : '';
// URL de la imagen del enchape servida desde MinIO (bucket enchapes)
const imgEnchapeUrl = (key) => key ? `${UPLOAD_URL}/catalogo/img/enchapes/${key}` : null;

// Normaliza texto: elimina tildes, minúsculas, colapsa espacios
const normalize = (s) => (s || '').toString()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().trim().replace(/\s+/g, ' ');

// Dropdown buscable — filtra proveedores por tipo/clasificación='proveedor'
const ProveedorSelect = ({ value, onChange, proveedores, disabled }) => {
  const [busqueda, setBusqueda] = useState('');
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const seleccionado = proveedores.find(p => p.id === value);

  const filtrados = useMemo(() => {
    if (!busqueda) return proveedores;
    const palabras = normalize(busqueda).split(' ').filter(Boolean);
    return proveedores.filter(p => {
      const texto = normalize([p.nombre, p.nit, p.contacto_nombre].filter(Boolean).join(' '));
      return palabras.every(w => texto.includes(w));
    });
  }, [busqueda, proveedores]);

  return (
    <div className="enchapes-prov-select" ref={ref}>
      <button
        type="button"
        className="enchapes-prov-trigger"
        onClick={() => setAbierto(a => !a)}
        disabled={disabled}
      >
        {seleccionado ? seleccionado.nombre : <span className="enchapes-prov-placeholder">— Sin proveedor —</span>}
        <span className="enchapes-prov-arrow">{abierto ? '▲' : '▼'}</span>
      </button>
      {abierto && (
        <div className="enchapes-prov-dropdown">
          <input
            type="text"
            className="enchapes-prov-search"
            placeholder="Buscar proveedor..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            autoFocus
          />
          <div className="enchapes-prov-list">
            <button
              type="button"
              className={`enchapes-prov-item ${!value ? 'selected' : ''}`}
              onClick={() => { onChange(null); setAbierto(false); setBusqueda(''); }}
            >
              <span className="enchapes-prov-item-name"><em>— Sin proveedor —</em></span>
            </button>
            {filtrados.length === 0 ? (
              <div className="enchapes-prov-empty">Sin resultados</div>
            ) : filtrados.map(p => (
              <button
                type="button"
                key={p.id}
                className={`enchapes-prov-item ${value === p.id ? 'selected' : ''}`}
                onClick={() => { onChange(p.id); setAbierto(false); setBusqueda(''); }}
              >
                <span className="enchapes-prov-item-name">{p.nombre}</span>
                {p.nit && <span className="enchapes-prov-item-nit">NIT: {p.nit}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Modal para buscar en la matriz y relacionar/quitar actividades de un enchape
const ActividadesModal = ({ enchape, matriz, onAdd, onRemove, onClose }) => {
  const [busqueda, setBusqueda] = useState('');
  const [guardando, setGuardando] = useState(false);

  const relacionadas = enchape.actividades || [];
  const idsRelacionadas = useMemo(() => new Set(relacionadas.map(a => a.id)), [relacionadas]);

  const resultados = useMemo(() => {
    const base = matriz.filter(m => !idsRelacionadas.has(m.id));
    if (!busqueda) return base.slice(0, 60);
    const palabras = normalize(busqueda).split(' ').filter(Boolean);
    return base.filter(m => {
      const texto = normalize([m.codigo, m.actividad, m.categoria, m.capitulo].filter(Boolean).join(' '));
      return palabras.every(w => texto.includes(w));
    }).slice(0, 60);
  }, [matriz, busqueda, idsRelacionadas]);

  const handleAdd = async (m) => {
    setGuardando(true);
    await onAdd(enchape.id, m);
    setGuardando(false);
  };

  return (
    <div className="enchapes-modal-overlay" onClick={onClose}>
      <div className="enchapes-modal" onClick={(e) => e.stopPropagation()}>
        <div className="enchapes-modal-header">
          <h3>Actividades — <span className="enchapes-modal-ref">{enchape.ref}</span></h3>
          <button className="enchapes-modal-close" onClick={onClose} title="Cerrar">&#10005;</button>
        </div>
        <div className="enchapes-modal-body">
          <div className="enchapes-modal-section">
            <label className="enchapes-modal-label">Relacionadas ({relacionadas.length})</label>
            {relacionadas.length === 0 ? (
              <p className="enchapes-modal-vacio">Aún no hay actividades relacionadas a este enchape.</p>
            ) : (
              <div className="enchapes-act-chips">
                {relacionadas.map(a => (
                  <span className="enchapes-act-chip" key={a.id} title={a.actividad}>
                    <span className="enchapes-act-chip-txt">{a.codigo ? `${a.codigo} · ` : ''}{a.actividad}</span>
                    <button className="enchapes-act-chip-x" title="Quitar" onClick={() => onRemove(enchape.id, a.id)}>&#10005;</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="enchapes-modal-section">
            <label className="enchapes-modal-label">Agregar actividad</label>
            <input
              className="enchapes-input"
              placeholder="Buscar por código, actividad, categoría o capítulo..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              autoFocus
            />
            <div className="enchapes-modal-resultados">
              {resultados.length === 0 ? (
                <div className="enchapes-act-empty">{busqueda ? 'Sin resultados' : 'Todas las actividades ya están relacionadas'}</div>
              ) : (
                resultados.map(m => (
                  <button className="enchapes-modal-result" key={m.id} disabled={guardando} onClick={() => handleAdd(m)}>
                    {m.codigo && <span className="enchapes-modal-result-cod">{m.codigo}</span>}
                    <span className="enchapes-modal-result-name">{m.actividad}</span>
                    <span className="enchapes-modal-result-meta">{[m.capitulo, m.categoria].filter(Boolean).join(' · ')}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
        <div className="enchapes-modal-footer">
          <button className="enchapes-btn-cancelar" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
};

const Enchapes = () => {
  const { success, error } = useNotification();

  const [enchapes, setEnchapes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [matriz, setMatriz] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [formData, setFormData] = useState({ ref: '', color: '', formato: '', precio: '', imagen: null, proveedor_id: null });
  const [subiendoImg, setSubiendoImg] = useState(false);
  // Enchape cuyo modal de actividades está abierto (null = cerrado)
  const [enchapeActivo, setEnchapeActivo] = useState(null);
  const fileRef = useRef(null);

  const cargarEnchapes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await client.query(`{ enchapes { id ref color formato precio imagen proveedor_id proveedor_nombre actividades { id codigo actividad categoria capitulo unidad } } }`);
      setEnchapes(data.enchapes || []);
    } catch (err) {
      error('Error de conexión al cargar enchapes');
    } finally {
      setLoading(false);
    }
  }, [error]);

  const cargarMatriz = useCallback(async () => {
    try {
      const data = await client.query(`{ matrizActividades { id codigo actividad categoria capitulo unidad } }`);
      setMatriz(data.matrizActividades || []);
    } catch (err) {
      error('Error al cargar la matriz de actividades');
    }
  }, [error]);

  const cargarProveedores = useCallback(async () => {
    try {
      const data = await client.query(`{ proveedores { proveedores { id nombre nit tipo clasificacion activo contacto_nombre } } }`);
      // Solo activos y marcados como proveedor (no contratista ni administrativo)
      const lista = (data.proveedores?.proveedores || []).filter(p => {
        if (p.activo === false) return false;
        const t = normalize(p.tipo);
        const c = normalize(p.clasificacion);
        return t === 'proveedor' || c === 'proveedor';
      }).sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
      setProveedores(lista);
    } catch (err) {
      error('Error al cargar proveedores');
    }
  }, [error]);

  useEffect(() => { cargarEnchapes(); cargarProveedores(); cargarMatriz(); }, [cargarEnchapes, cargarProveedores, cargarMatriz]);

  const resetForm = () => {
    setFormData({ ref: '', color: '', formato: '', precio: '', imagen: null, proveedor_id: null });
    setEditandoId(null);
    setMostrarForm(false);
  };

  const iniciarEdicion = (enchape) => {
    setFormData({
      ref: enchape.ref || '',
      color: enchape.color || '',
      formato: enchape.formato || '',
      precio: enchape.precio || '',
      imagen: enchape.imagen || null,
      proveedor_id: enchape.proveedor_id || null,
    });
    setEditandoId(enchape.id);
    setMostrarForm(true);
  };

  // Subir imagen del enchape al bucket "enchapes" y guardar su key en formData
  const subirImagen = async (file) => {
    if (!file) return;
    setSubiendoImg(true);
    try {
      const token = localStorage.getItem('tuvi_token');
      const fd = new FormData();
      fd.append('files', file);
      const res = await fetch(`${UPLOAD_URL}/catalogo/upload?prefix=&bucket=enchapes`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (data.files?.[0]?.key) {
        setFormData(prev => ({ ...prev, imagen: data.files[0].key }));
        success('Imagen cargada');
      } else { error('No se pudo subir la imagen'); }
    } catch (e) { error('Error al subir la imagen'); }
    finally { setSubiendoImg(false); }
  };

  const guardar = async () => {
    if (!formData.ref.trim()) { error('La referencia es obligatoria'); return; }
    try {
      const precioNum = formData.precio === '' || formData.precio == null ? 0 : parseFloat(formData.precio) || 0;
      if (editandoId) {
        const r = await client.mutate(
          `mutation($id: Int!, $ref: String, $color: String, $formato: String, $precio: Float, $imagen: String, $proveedor_id: Int) { editarEnchape(id: $id, ref: $ref, color: $color, formato: $formato, precio: $precio, imagen: $imagen, proveedor_id: $proveedor_id) { success message } }`,
          { id: parseInt(editandoId), ref: formData.ref, color: formData.color, formato: formData.formato, precio: precioNum, imagen: formData.imagen, proveedor_id: formData.proveedor_id }
        );
        if (r.editarEnchape.success) { success('Enchape actualizado'); resetForm(); cargarEnchapes(); }
        else error(r.editarEnchape.message);
      } else {
        const r = await client.mutate(
          `mutation($ref: String!, $color: String, $formato: String, $precio: Float, $imagen: String, $proveedor_id: Int) { crearEnchape(ref: $ref, color: $color, formato: $formato, precio: $precio, imagen: $imagen, proveedor_id: $proveedor_id) { success message } }`,
          { ref: formData.ref, color: formData.color, formato: formData.formato, precio: precioNum, imagen: formData.imagen, proveedor_id: formData.proveedor_id }
        );
        if (r.crearEnchape.success) { success('Enchape creado'); resetForm(); cargarEnchapes(); }
        else error(r.crearEnchape.message);
      }
    } catch (err) { error('Error al guardar'); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('Eliminar este enchape?')) return;
    try {
      const r = await client.mutate(`mutation($id: Int!) { eliminarEnchape(id: $id) { success message } }`, { id });
      if (r.eliminarEnchape.success) { success('Enchape eliminado'); cargarEnchapes(); }
      else error(r.eliminarEnchape.message);
    } catch (err) { error('Error al eliminar'); }
  };

  // Relacionar una actividad de la matriz a un enchape
  const agregarActividad = async (enchapeId, act) => {
    try {
      const r = await client.mutate(
        `mutation($e: Int!, $m: Int!) { agregarActividadEnchape(enchape_id: $e, matriz_actividad_id: $m) { success message } }`,
        { e: enchapeId, m: act.id }
      );
      if (r.agregarActividadEnchape.success) {
        setEnchapes(prev => prev.map(en => {
          if (en.id !== enchapeId) return en;
          const yaEsta = (en.actividades || []).some(a => a.id === act.id);
          if (yaEsta) return en;
          const nueva = { id: act.id, codigo: act.codigo, actividad: act.actividad, categoria: act.categoria, capitulo: act.capitulo, unidad: act.unidad };
          const actividades = [...(en.actividades || []), nueva].sort((a, b) => (a.codigo || '').localeCompare(b.codigo || ''));
          return { ...en, actividades };
        }));
      } else error(r.agregarActividadEnchape.message);
    } catch (err) { error('Error al relacionar la actividad'); }
  };

  // Quitar la relación de una actividad de un enchape
  const quitarActividad = async (enchapeId, actividadId) => {
    try {
      const r = await client.mutate(
        `mutation($e: Int!, $m: Int!) { quitarActividadEnchape(enchape_id: $e, matriz_actividad_id: $m) { success message } }`,
        { e: enchapeId, m: actividadId }
      );
      if (r.quitarActividadEnchape.success) {
        setEnchapes(prev => prev.map(en => en.id === enchapeId
          ? { ...en, actividades: (en.actividades || []).filter(a => a.id !== actividadId) }
          : en));
      } else error(r.quitarActividadEnchape.message);
    } catch (err) { error('Error al quitar la actividad'); }
  };

  const enchapesFiltrados = useMemo(() => {
    if (!busqueda) return enchapes;
    const palabras = normalize(busqueda).split(' ').filter(Boolean);
    return enchapes.filter(e => {
      const actsTxt = (e.actividades || []).map(a => `${a.codigo || ''} ${a.actividad || ''}`).join(' ');
      const texto = normalize([e.ref, e.color, e.formato, e.proveedor_nombre, actsTxt].filter(Boolean).join(' '));
      return palabras.every(w => texto.includes(w));
    });
  }, [enchapes, busqueda]);

  return (
    <div className="enchapes-container">
      <div className="enchapes-header">
        <div>
          <h2 className="enchapes-title">Enchapes</h2>
          <p className="enchapes-subtitle">{enchapes.length} enchapes registrados</p>
        </div>
        <button className="enchapes-btn-crear" onClick={() => { resetForm(); setMostrarForm(true); }}>+ Nuevo Enchape</button>
      </div>

      {mostrarForm && (
        <div className="enchapes-form">
          <h3 className="enchapes-form-title">{editandoId ? 'Editar Enchape' : 'Nuevo Enchape'}</h3>
          <div className="enchapes-form-grid">
            <div className="enchapes-form-group">
              <label>Ref *</label>
              <input type="text" className="enchapes-input" value={formData.ref}
                onChange={(e) => setFormData(prev => ({ ...prev, ref: e.target.value }))}
                placeholder="Referencia del enchape" autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') guardar(); if (e.key === 'Escape') resetForm(); }} />
            </div>
            <div className="enchapes-form-group">
              <label>Color</label>
              <input type="text" className="enchapes-input" value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                placeholder="Color del enchape"
                onKeyDown={(e) => { if (e.key === 'Enter') guardar(); if (e.key === 'Escape') resetForm(); }} />
            </div>
            <div className="enchapes-form-group">
              <label>Formato</label>
              <input type="text" className="enchapes-input" value={formData.formato}
                onChange={(e) => setFormData(prev => ({ ...prev, formato: e.target.value }))}
                placeholder="Ej: 60x60, 30x60..."
                onKeyDown={(e) => { if (e.key === 'Enter') guardar(); if (e.key === 'Escape') resetForm(); }} />
            </div>
            <div className="enchapes-form-group">
              <label>Precio</label>
              <input type="number" className="enchapes-input" value={formData.precio}
                onChange={(e) => setFormData(prev => ({ ...prev, precio: e.target.value }))}
                placeholder="0" min="0" step="any"
                onKeyDown={(e) => { if (e.key === 'Enter') guardar(); if (e.key === 'Escape') resetForm(); }} />
            </div>
            <div className="enchapes-form-group">
              <label>Proveedor</label>
              <ProveedorSelect
                value={formData.proveedor_id}
                onChange={(id) => setFormData(prev => ({ ...prev, proveedor_id: id }))}
                proveedores={proveedores}
              />
            </div>
            <div className="enchapes-form-group">
              <label>Imagen</label>
              <div className="enchapes-img-field">
                {formData.imagen && (
                  <img src={imgEnchapeUrl(formData.imagen)} alt="enchape" className="enchapes-img-preview" />
                )}
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={(e) => subirImagen(e.target.files[0])} />
                <button type="button" className="enchapes-btn-img" onClick={() => fileRef.current?.click()} disabled={subiendoImg}>
                  {subiendoImg ? 'Subiendo…' : formData.imagen ? 'Cambiar imagen' : 'Subir imagen'}
                </button>
                {formData.imagen && (
                  <button type="button" className="enchapes-btn-img-quitar" onClick={() => setFormData(prev => ({ ...prev, imagen: null }))}>Quitar</button>
                )}
              </div>
            </div>
          </div>
          <div className="enchapes-form-actions">
            <button className="enchapes-btn-guardar" onClick={guardar}>{editandoId ? 'Actualizar' : 'Guardar'}</button>
            <button className="enchapes-btn-cancelar" onClick={resetForm}>Cancelar</button>
          </div>
        </div>
      )}

      <div className="enchapes-busqueda">
        <input type="text" className="enchapes-input" value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar por referencia, color, formato o proveedor..." />
      </div>

      {loading ? (
        <div className="enchapes-loading">Cargando enchapes...</div>
      ) : enchapesFiltrados.length === 0 ? (
        <div className="enchapes-empty">{busqueda ? 'No se encontraron enchapes' : 'No hay enchapes registrados'}</div>
      ) : (
        <div className="enchapes-table-container">
          <table className="enchapes-table">
            <thead><tr><th>Ref</th><th>Color</th><th>Formato</th><th>Precio</th><th>Imagen</th><th>Proveedor</th><th>Actividades</th><th>Acciones</th></tr></thead>
            <tbody>
              {enchapesFiltrados.map(enchape => (
                <tr key={enchape.id}>
                  <td className="enchapes-td-ref">{enchape.ref}</td>
                  <td>{enchape.color}</td>
                  <td>{enchape.formato}</td>
                  <td>{enchape.precio > 0 ? formatMoney(enchape.precio) : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                  <td>
                    {enchape.imagen
                      ? <a href={imgEnchapeUrl(enchape.imagen)} target="_blank" rel="noopener noreferrer"><img src={imgEnchapeUrl(enchape.imagen)} alt={enchape.ref} className="enchapes-td-img" /></a>
                      : <span style={{ color: '#cbd5e1' }}>—</span>}
                  </td>
                  <td>{enchape.proveedor_nombre || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                  <td className="enchapes-td-actividades">
                    <div className="enchapes-act-cell">
                      <div className="enchapes-act-chips">
                        {(enchape.actividades || []).length === 0
                          ? <span className="enchapes-act-vacio">Sin actividades</span>
                          : enchape.actividades.map(a => (
                            <span className="enchapes-act-chip" key={a.id} title={a.actividad}>
                              <span className="enchapes-act-chip-txt">{a.codigo ? `${a.codigo} · ` : ''}{a.actividad}</span>
                              <button className="enchapes-act-chip-x" title="Quitar" onClick={() => quitarActividad(enchape.id, a.id)}>&#10005;</button>
                            </span>
                          ))}
                      </div>
                      <button className="enchapes-act-add" onClick={() => setEnchapeActivo(enchape.id)}>+ Actividad</button>
                    </div>
                  </td>
                  <td className="enchapes-td-acciones">
                    <button className="enchapes-btn-editar" onClick={() => iniciarEdicion(enchape)} title="Editar">&#9998;</button>
                    <button className="enchapes-btn-eliminar" onClick={() => eliminar(enchape.id)} title="Eliminar">&#10005;</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {enchapeActivo != null && (() => {
        const en = enchapes.find(e => e.id === enchapeActivo);
        if (!en) return null;
        return (
          <ActividadesModal
            enchape={en}
            matriz={matriz}
            onAdd={agregarActividad}
            onRemove={quitarActividad}
            onClose={() => setEnchapeActivo(null)}
          />
        );
      })()}
    </div>
  );
};

export default Enchapes;
